/**
 * SourceTypeSelector - Toggle between dataset and view source types for JOINs
 *
 * Allows users to switch between selecting a dataset or a view as the JOIN source.
 * Uses Tabs component for a clean, accessible toggle interface.
 */

import React from 'react';
import { Database, Eye } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ViewJoinHelp, DatasetJoinHelp } from '../HelpTooltips';

export interface SourceTypeSelectorProps {
  value: 'dataset' | 'view';
  onChange: (type: 'dataset' | 'view') => void;
  disabled?: boolean;
}

export const SourceTypeSelector: React.FC<SourceTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  return (
    <div className="space-y-2">
      <Label htmlFor="source-type-selector" className="flex items-center gap-2">
        Source Type
        {value === 'dataset' ? <DatasetJoinHelp /> : <ViewJoinHelp />}
      </Label>
      <Tabs
        value={value}
        onValueChange={(val) => onChange(val as 'dataset' | 'view')}
        id="source-type-selector"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger
            value="dataset"
            disabled={disabled}
            aria-label="Select dataset as source"
            className="flex items-center gap-2"
          >
            <Database className="h-4 w-4" />
            Dataset
          </TabsTrigger>
          <TabsTrigger
            value="view"
            disabled={disabled}
            aria-label="Select view as source"
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            View
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <p className="text-xs text-muted-foreground">
        {value === 'dataset'
          ? 'Join against a dataset containing raw data'
          : 'Join against a view with transformations and filters'}
      </p>
    </div>
  );
};
